import { useEffect, useRef, useState } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { useLocation } from 'wouter';

import { commandPaletteContext, commandPaletteOpen, isClearSiteDataShortcut, isCommandPaletteShortcut, type CommandPaletteCommand } from '@/shared/utils/command-palette';
import { loadAppData, loadHAPOnly } from '@/app/api/app-api';
import { clearSiteData, logoutSession, sessionUser } from '@/pages/authentication/state/session';

import Button from './Button';
import Input from './Input';


const navigationCommands: Array<Omit<CommandPaletteCommand, 'run'>> = [
  { id: 'navigate-beds', label: 'Open Beds', keywords: ['home', 'bed board', 'open bed', 'openbed'] },
  { id: 'navigate-patients', label: 'Open Patients', keywords: ['patient list'] },
  { id: 'navigate-events', label: 'Open Events', keywords: ['activity'] },
  { id: 'navigate-report', label: 'Open Reports', keywords: ['status report', 'print'] },
  { id: 'navigate-bed-board', label: 'Open Bed Board', keywords: ['bed print', 'board', 'pdf'] },
  { id: 'navigate-status-report', label: 'Open Status Report', keywords: ['report print', 'status', 'pdf'] },
  { id: 'navigate-hap', label: 'Open HAP', keywords: ['hap', 'planning'] },
  { id: 'navigate-statistics', label: 'Open Statistics', keywords: ['stats', 'statistics'] },
];

const managerCommands: Array<Omit<CommandPaletteCommand, 'run'>> = [
  { id: 'navigate-manage-ovd', label: 'Open Manage OVD', keywords: ['manage', 'afdeling'] },
  { id: 'navigate-manage-beds', label: 'Open Manage Beds', keywords: ['manage', 'bed configuration'] },
  { id: 'navigate-manage-settings', label: 'Open Manage Settings', keywords: ['manage', 'settings'] },
  { id: 'navigate-manage-backup', label: 'Open Manage Backup', keywords: ['manage', 'restore'] },
];

const administratorCommands: Array<Omit<CommandPaletteCommand, 'run'>> = [
  { id: 'navigate-manage-users', label: 'Open Manage Users', keywords: ['manage', 'accounts'] },
  { id: 'navigate-manage-analytics', label: 'Open Manage Analytics', keywords: ['manage', 'usage'] },
  { id: 'navigate-manage-admin', label: 'Open Manage Admin', keywords: ['manage', 'production data', 'sync'] },
  { id: 'navigate-manage-reset', label: 'Open Manage Reset', keywords: ['manage', 'reset data'] },
];

export const sessionCommands: Array<Omit<CommandPaletteCommand, 'run'>> = [
  { id: 'session-logout', label: 'Log Out', description: 'End current session', keywords: ['sign out', 'logout'] },
  { id: 'session-clear-site-data', label: 'Clear Site Data', description: 'Sign out and clear cookies and browser storage', keywords: ['reset', 'cookies', 'local storage', 'session storage', 'cache'] },
];

const navigationPaths: Record<string, string> = {
  'navigate-beds': '/beds',
  'navigate-patients': '/patients',
  'navigate-events': '/events',
  'navigate-report': '/report',
  'navigate-bed-board': '/report/print/bed-board',
  'navigate-status-report': '/report/print/status-report',
  'navigate-hap': '/hap',
  'navigate-statistics': '/statistics',
  'navigate-manage-ovd': '/manage/ovd',
  'navigate-manage-beds': '/manage/beds',
  'navigate-manage-settings': '/manage/settings',
  'navigate-manage-backup': '/manage/backup',
  'navigate-manage-users': '/manage/users',
  'navigate-manage-analytics': '/manage/analytics',
  'navigate-manage-admin': '/manage/admin',
  'navigate-manage-reset': '/manage/reset',
};

function isSearchCharacter(value: string) {
  return /[\p{L}\p{N}]/u.test(value);
}

export function getFuzzyMatchIndices(query: string, text: string) {
  const queryCharacters = Array.from(query.toLowerCase()).filter(isSearchCharacter);
  if (!queryCharacters.length) return [];

  const textCharacters = Array.from(text);
  const compactQuery = queryCharacters.join('');
  const compactText = textCharacters.filter(isSearchCharacter).map((character) => character.toLowerCase());
  const compactStart = compactText.join('').indexOf(compactQuery);
  if (compactStart >= 0) {
    const compactIndices: number[] = [];
    let compactIndex = 0;
    for (let textIndex = 0; textIndex < textCharacters.length && compactIndex < compactStart + compactQuery.length; textIndex += 1) {
      if (isSearchCharacter(textCharacters[textIndex])) {
        if (compactIndex >= compactStart) compactIndices.push(textIndex);
        compactIndex += 1;
      }
    }
    return compactIndices;
  }

  const matchIndices: number[] = [];
  let queryIndex = 0;
  for (let textIndex = 0; textIndex < textCharacters.length && queryIndex < queryCharacters.length; textIndex += 1) {
    const character = textCharacters[textIndex].toLowerCase();
    if (isSearchCharacter(character) && character === queryCharacters[queryIndex]) {
      matchIndices.push(textIndex);
      queryIndex += 1;
    }
  }

  return queryIndex === queryCharacters.length ? matchIndices : null;
}

export function matchesCommand(query: string, command: CommandPaletteCommand) {
  if (!query.trim()) return true;
  return [command.label, command.description ?? '', ...(command.keywords ?? [])]
    .some((field) => getFuzzyMatchIndices(query, field) !== null);
}

function HighlightedText({ text, matchIndices }: { text: string; matchIndices: number[] | null }) {
  if (!matchIndices?.length) return <>{text}</>;
  const matchSet = new Set(matchIndices);
  return <>{Array.from(text).map((character, index) => matchSet.has(index) ? <span key={index} className="bg-amber-200 text-inherit">{character}</span> : character)}</>;
}

function getCommandHighlights(query: string, command: CommandPaletteCommand) {
  return {
    label: getFuzzyMatchIndices(query, command.label),
    description: command.description ? getFuzzyMatchIndices(query, command.description) : null,
  };
}

export default function CommandPalette() {
  useSignals();
  const [, navigate] = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const signedIn = Boolean(sessionUser.value);
  const isHAP = sessionUser.value?.role === 'HAP';
  const isHandover = sessionUser.value?.role === 'Handover' || Boolean(sessionUser.value?.handover_department_id);
  const open = commandPaletteOpen.value;
  const commands = !signedIn ? [] : [
    ...commandPaletteContext.value,
    ...sessionCommands.map((command) => ({
      ...command,
      run: () => {
        if (command.id === 'session-logout') {
          void logoutSession();
          return;
        }
        if (window.confirm('Clear all site data for this application and sign out? This cannot be undone.')) {
          void clearSiteData();
        }
      },
    })),
    { id: 'reload-data', label: 'Refresh Data', description: 'Reload current application data', keywords: ['reload', 'sync'], run: () => void (isHAP ? loadHAPOnly() : loadAppData()) },
    { id: 'reload-application', label: 'Reload Application', description: 'Reload the HOH application', keywords: ['refresh', 'browser'], run: () => window.location.reload() },
    ...[(isHAP ? navigationCommands.filter((command) => command.id === 'navigate-hap') : navigationCommands), ...managerCommands.filter(() => sessionUser.value?.role === 'Manager' || sessionUser.value?.role === 'Administrator'), ...administratorCommands.filter(() => sessionUser.value?.role === 'Administrator')].flat().map((command) => ({ ...command, run: () => navigate(navigationPaths[command.id]) })),
  ];
  const filteredCommands = commands.filter((command) => (!isHandover || command.id.startsWith('session-') || command.id === 'reload-application') && matchesCommand(query, command));

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if (isClearSiteDataShortcut(event)) {
        if (event.repeat) return;
        event.preventDefault();
        if (window.confirm('Clear all site data for this application and sign out? This cannot be undone.')) {
          void clearSiteData();
        }
        return;
      }

      if (isCommandPaletteShortcut(event)) {
        event.preventDefault();
        commandPaletteOpen.value = !commandPaletteOpen.value;
      }
    }

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
      return;
    }
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!open) return null;

  function close() {
    commandPaletteOpen.value = false;
  }

  function run(command: CommandPaletteCommand) {
    close();
    command.run();
  }

  function handleKeydown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((index) => Math.min(index + 1, filteredCommands.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter' && filteredCommands[selectedIndex]) {
      event.preventDefault();
      run(filteredCommands[selectedIndex]);
    }
  }

  return <div className="fixed inset-0 z-200 flex items-start justify-center bg-slate-950/35 px-4 pt-[15vh]" role="presentation" onMouseDown={close}>
    <section role="dialog" aria-modal="true" aria-label="Command palette" className="w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
      <Input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={handleKeydown} placeholder="Type a command..." aria-label="Search commands" className="h-14 w-full border-b border-slate-200 px-4 text-base font-medium text-slate-900 outline-none placeholder:text-slate-400" />
      <div className="max-h-80 overflow-y-auto p-2">
        {filteredCommands.length ? filteredCommands.map((command, index) => { const highlights = getCommandHighlights(query, command); return <Button variant="unstyled" key={command.id} type="button" onClick={() => run(command)} className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm ${index === selectedIndex ? 'bg-sky-50 text-sky-950' : 'text-slate-700 hover:bg-slate-50'}`}><span><span className="block font-semibold"><HighlightedText text={command.label} matchIndices={highlights.label} /></span>{command.description ? <span className="block text-xs text-slate-500"><HighlightedText text={command.description} matchIndices={highlights.description} /></span> : null}</span>{index === selectedIndex ? <span className="text-xs font-semibold text-sky-700">Enter</span> : null}</Button>; }) : <p className="px-3 py-6 text-center text-sm text-slate-500">No commands found.</p>}
      </div>
      <div className="flex justify-end gap-3 border-t border-slate-100 px-4 py-2 text-xs text-slate-500"><span>↑↓ Navigate</span><span>Enter Run</span><span>Esc Close</span></div>
    </section>
  </div>;
}
